/**
 * @file src/router.tsx
 * @exports createAppRouter
 * @summary Code-based TanStack Router route tree mirroring the former Next.js `src/pages/` filesystem routes.
 * @remarks Every URL is preserved exactly as it was under Next.js (Next `[param]` folders become
 *          `$param` segments). Page modules stay in `src/pages/` and are code-split via
 *          `lazyRouteComponent`. Search params use TanStack Router's default JSON-aware
 *          serialization. The `/` index and legacy `/vid/remote-id-upload` routes issue the
 *          redirects previously handled by `src/middleware.ts` and `next.config.js`. Error handling
 *          (fallback page and Sentry report for any route that throws) comes from
 *          `src/lib/routerErrorHandling`.
 */

import { createRootRoute, createRoute, createRouter, lazyRouteComponent, redirect } from "@tanstack/react-router";
import type React from "react";

import { App } from "src/App";
import { paths } from "src/const";
import { routerErrorHandling } from "src/lib/routerErrorHandling";
import NotFoundPage from "src/pages/404";

const rootRoute = createRootRoute({
	component: App,
	notFoundComponent: NotFoundPage,
});

type PageLoader = () => Promise<{ default: React.ComponentType }>;

const page = (path: string, loader: PageLoader) =>
	createRoute({
		component: lazyRouteComponent(loader),
		getParentRoute: () => rootRoute,
		path,
	});

const indexRoute = createRoute({
	beforeLoad: ({ search }) => {
		throw redirect({ replace: true, search, to: paths.home });
	},
	getParentRoute: () => rootRoute,
	path: "/",
});

/** Permanent redirect formerly declared in `next.config.js`. */
const legacyRemoteIdUploadRoute = createRoute({
	beforeLoad: ({ search }) => {
		throw redirect({ replace: true, search, to: paths.vidRemoteIdUpload });
	},
	getParentRoute: () => rootRoute,
	path: "/vid/remote-id-upload",
});

const routeTree = rootRoute.addChildren([
	indexRoute,
	legacyRemoteIdUploadRoute,
	page("/404", () => import("src/pages/404")),
	page("/api-keys", () => import("src/pages/api-keys")),
	page("/audit-log", () => import("src/pages/audit-log")),
	page("/auth/sso/direct", () => import("src/pages/auth/sso/direct")),
	page("/create-invite", () => import("src/pages/create-invite")),
	page("/credential", () => import("src/pages/credential/index")),
	page("/credential/$data", () => import("src/pages/credential/[data]")),
	page("/dispute/$orgName/$orderId", () => import("src/pages/dispute/[orgName]/[orderId]")),
	page("/dispute/$orgName/$orderId/report", () => import("src/pages/dispute/[orgName]/[orderId]/report")),
	page("/dispute/$orgName/auth/$orderId", () => import("src/pages/dispute/[orgName]/auth/[orderId]/index")),
	page("/dispute/$orgName/auth/$orderId/validate", () => import("src/pages/dispute/[orgName]/auth/[orderId]/validate")),
	page("/dispute/$orgName/thank-you", () => import("src/pages/dispute/[orgName]/thank-you")),
	page("/invites", () => import("src/pages/invites")),
	page("/login", () => import("src/pages/login")),
	page("/logout", () => import("src/pages/logout")),
	page("/mfa", () => import("src/pages/mfa")),
	page("/organizations", () => import("src/pages/organizations/index")),
	page("/organizations/$id", () => import("src/pages/organizations/[id]")),
	page("/organizations/$id/adverse-action-email", () => import("src/pages/organizations/[id]/adverse-action-email")),
	page("/pass/$passId", () => import("src/pages/pass/[passId]")),
	page("/post-registration", () => import("src/pages/post-registration")),
	page("/profile-settings", () => import("src/pages/profile-settings")),
	page("/quick-login", () => import("src/pages/quick-login")),
	page("/quick-login-code", () => import("src/pages/quick-login-code")),
	page("/quick-registration", () => import("src/pages/quick-registration")),
	page("/quick-registration-code", () => import("src/pages/quick-registration-code")),
	page("/recovery", () => import("src/pages/recovery")),
	page("/recovery-link-sent", () => import("src/pages/recovery-link-sent")),
	page("/registration", () => import("src/pages/registration")),
	page("/remote/id-upload", () => import("src/pages/remote/id-upload")),
	page("/reports", () => import("src/pages/reports")),
	page("/resend-reset-code", () => import("src/pages/resend-reset-code")),
	page("/settings", () => import("src/pages/settings")),
	page("/suborg", () => import("src/pages/suborg")),
	page("/svix", () => import("src/pages/svix")),
	page("/user/$id", () => import("src/pages/user/[id]/index")),
	page("/user/$id/$passId", () => import("src/pages/user/[id]/[passId]")),
	page("/users", () => import("src/pages/users")),
	page("/users/$id/edit", () => import("src/pages/users/[id]/edit")),
	page("/verification", () => import("src/pages/verification")),
	page("/vid/additional-information/$beamId", () => import("src/pages/vid/additional-information/[beamId]")),
	page("/vid/almost-done", () => import("src/pages/vid/almost-done")),
	page("/vid/expired", () => import("src/pages/vid/expired")),
	page("/vid/feedback", () => import("src/pages/vid/feedback")),
	page("/vid/i/$", () => import("src/pages/vid/i/[invite]")),
	page("/vid/id-upload", () => import("src/pages/vid/id-upload")),
	page("/vid/international-id-type", () => import("src/pages/vid/international-id-type")),
	page("/vid/landing", () => import("src/pages/vid/landing")),
	page("/vid/manual-entry", () => import("src/pages/vid/manual-entry")),
	page("/vid/personal-information", () => import("src/pages/vid/personal-information")),
	page("/vid/remote-capture-init", () => import("src/pages/vid/remote-capture-init")),
	page("/vid/select-id-type", () => import("src/pages/vid/select-id-type")),
	page("/vid/selfie", () => import("src/pages/vid/selfie")),
	page("/vid/service-unavailable", () => import("src/pages/vid/service-unavailable")),
	page("/vid/sign-disclosures", () => import("src/pages/vid/sign-disclosures")),
	page("/vid/ssn", () => import("src/pages/vid/ssn")),
	page("/vid/success", () => import("src/pages/vid/success")),
	page("/vid/too-many-scan-attempts", () => import("src/pages/vid/too-many-scan-attempts")),
	page("/vid/us-id-type", () => import("src/pages/vid/us-id-type")),
	page("/vid/verify-email", () => import("src/pages/vid/verify-email")),
	page("/vid/welcome", () => import("src/pages/vid/welcome")),
	page("/wallet-share/$token", () => import("src/pages/wallet-share/[token]")),
]);

/** Builds the application router. Called once from `src/main.tsx`. */
export const createAppRouter = () =>
	createRouter({
		...routerErrorHandling,
		defaultPreload: false,
		routeTree,
	});

type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
	interface Register {
		router: AppRouter;
	}
}
